"use client"

import { useState, useRef, useCallback } from "react"
import { Download, Star, Heart } from "lucide-react"
import { formatDownloads, type ClawHubSkill } from "@/lib/clawhub-api"
import { skillInteraction } from "@/lib/clawhub-api"
import { useToast } from "@/lib/hooks/use-toast"
import { spawnEmojiFirework } from "@/lib/emoji-firework"
import "./skill-card.less"

export function SkillCard({
  skill,
  onClick,
  onTagClick,
  delay,
  compact = false,
  onSkillUpdate,
}: {
  skill: ClawHubSkill
  onClick: () => void
  onTagClick: (tag: string) => void
  delay: number
  compact?: boolean
  /** 乐观更新回调：由父组件更新 skill 数据驱动 UI */
  onSkillUpdate?: (updated: ClawHubSkill) => void
}) {
  const toast = useToast()
  const likePending = useRef(false)
  const collectPending = useRef(false)

  const [optimisticLiked, setOptimisticLiked] = useState<boolean | null>(null)
  const [optimisticCollected, setOptimisticCollected] = useState<boolean | null>(null)
  const [optimisticStars, setOptimisticStars] = useState<number | null>(null)
  const [optimisticCollectNum, setOptimisticCollectNum] = useState<number | null>(null)

  // prop 变化时重置乐观状态，让 prop 接管
  const [prevId, setPrevId] = useState(skill.id)
  const [prevLike, setPrevLike] = useState(skill.isLike)
  const [prevCollect, setPrevCollect] = useState(skill.isCollect)
  if (
    skill.id !== prevId ||
    skill.isLike !== prevLike ||
    skill.isCollect !== prevCollect
  ) {
    setPrevId(skill.id)
    setPrevLike(skill.isLike)
    setPrevCollect(skill.isCollect)
    setOptimisticLiked(null)
    setOptimisticCollected(null)
    setOptimisticStars(null)
    setOptimisticCollectNum(null)
  }

  const isLiked = optimisticLiked ?? skill.isLike ?? false
  const isCollected = optimisticCollected ?? skill.isCollect ?? false
  const stars = optimisticStars ?? skill.stars
  const collectNum = optimisticCollectNum ?? skill.collectNum ?? 0

  const handleLike = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (likePending.current) return
    likePending.current = true
    const wasLiked = skill.isLike ?? false
    const delta = wasLiked ? -1 : 1
    // 本地乐观更新（即时反馈）
    setOptimisticLiked(!wasLiked)
    setOptimisticStars(Math.max(0, skill.stars + delta))
    if (!wasLiked) {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      spawnEmojiFirework(rect.left + rect.width / 2, rect.top + rect.height / 2, "❤️")
    }
    try {
      await skillInteraction({
        skillKey: skill.skillKey ?? skill.id,
        type: "like",
        action: wasLiked ? "cancel" : "click",
      })
      // 成功后通知父组件更新数据源（用原始 prop 值计算，避免双重计数）
      onSkillUpdate?.({
        ...skill,
        isLike: !wasLiked,
        stars: Math.max(0, skill.stars + delta),
      })
    } catch (err) {
      // 失败回滚本地状态
      setOptimisticLiked(wasLiked)
      setOptimisticStars(skill.stars)
      if (err instanceof Error && err.message.includes("频繁")) {
        toast.error(err.message)
      }
    } finally {
      likePending.current = false
    }
  }, [skill, onSkillUpdate, toast])

  const handleCollect = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (collectPending.current) return
    collectPending.current = true
    const wasCollected = skill.isCollect ?? false
    const delta = wasCollected ? -1 : 1
    // 本地乐观更新
    setOptimisticCollected(!wasCollected)
    setOptimisticCollectNum(Math.max(0, (skill.collectNum ?? 0) + delta))
    if (!wasCollected) {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      spawnEmojiFirework(rect.left + rect.width / 2, rect.top + rect.height / 2, "⭐")
    }
    try {
      await skillInteraction({
        skillKey: skill.skillKey ?? skill.id,
        type: "collect",
        action: wasCollected ? "cancel" : "click",
      })
      onSkillUpdate?.({
        ...skill,
        isCollect: !wasCollected,
        collectNum: Math.max(0, (skill.collectNum ?? 0) + delta),
      })
    } catch (err) {
      setOptimisticCollected(wasCollected)
      setOptimisticCollectNum(skill.collectNum ?? 0)
      if (err instanceof Error && err.message.includes("频繁")) {
        toast.error(err.message)
      }
    } finally {
      collectPending.current = false
    }
  }, [skill, onSkillUpdate, toast])

  return (
    <div
      className="skill-card"
      style={{ animationDelay: `${delay}ms` }}
      onClick={onClick}
    >
      <div className={`skill-card__body ${compact ? "skill-card__body--compact" : "skill-card__body--normal"}`}>
        {/* Header: Title + badges */}
        <div className="flex items-start justify-between gap-3 mb-1.5">
          <h3 className={`skill-card__title ${compact ? "skill-card__title--compact" : "skill-card__title--normal"}`}>
            {skill.name}
          </h3>
          <div className="flex items-center gap-1.5 shrink-0">
            {skill.isPaid === 1 && skill.isBuy ? (
              <span className="skill-card__bought-badge">
                已购买
              </span>
            ) : skill.isPaid === 1 ? (
              <span className="skill-card__paid-badge">
                ¥{skill.price?.toFixed(2) ?? "—"}
              </span>
            ) : (
              <span className="skill-card__free-badge">
                免费
              </span>
            )}
          </div>
        </div>

        {/* Type label */}
        <p className="skill-card__type">
          技能{skill.version && skill.version !== "—" ? ` · ${skill.version.startsWith("v") ? "" : "v"}${skill.version}` : ""}
        </p>

        {/* Description */}
        <p className={`skill-card__desc ${compact ? "skill-card__desc--compact" : "skill-card__desc--normal"} ${skill.tags.length > 0 ? "skill-card__desc--has-tags" : ""}`}>
          {skill.description}
        </p>

        {/* Tags */}
        {skill.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3.5">
            {skill.tags.slice(0, 3).map((tag, i) => (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation()
                  onTagClick(tag)
                }}
                className="skill-card__tag"
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        {/* Spacer pushes footer to bottom */}
        <div className="flex-1" />

        {/* Footer: Author left, stats right */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="skill-card__avatar">
              {skill.avatarUrl ? (
                <img src={skill.avatarUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="select-none" aria-hidden>{skill.author?.charAt(0) ?? "?"}</span>
              )}
            </div>
            <span className="skill-card__author">
              {skill.author}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={`skill-card__stat-badge ${isLiked ? "skill-card__stat-badge--like-active" : "skill-card__stat-badge--like"}`}
              onClick={handleLike}
            >
              <Heart className={`h-3 w-3 ${isLiked ? "fill-current" : ""}`} />
              {formatDownloads(stars)}
            </button>
            <button
              type="button"
              className={`skill-card__stat-badge ${isCollected ? "skill-card__stat-badge--collect-active" : "skill-card__stat-badge--collect"}`}
              onClick={handleCollect}
            >
              <Star className={`h-3 w-3 ${isCollected ? "fill-current" : ""}`} />
              {formatDownloads(collectNum)}
            </button>
            <span className="skill-card__download-badge">
              <Download className="h-3 w-3" />
              {formatDownloads(skill.downloads)}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
